/**
 * LoggerManager - Центральний менеджер логування системи
 */

import { observable, action, makeObservable, runInAction } from 'mobx'

export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'

export interface LogEntry {
    id: string
    timestamp: number
    level: LogLevel
    message: string
    category: string
    context?: string
    data?: any
    error?: {
        name: string
        message: string
        stack?: string
    }
    tags: string[]
    sessionId?: string
}

export interface LogCategory {
    name: string
    level: LogLevel
    enabled: boolean
    color?: string
    description?: string
}

export interface LogStats {
    total: number
    byLevel: Record<LogLevel, number>
    byCategory: Record<string, number>
    errors: number
    warnings: number
    recentActivity: LogEntry[]
}

/**
 * Центральний менеджер логування
 */
export class LoggerManager {
    private static instance: LoggerManager

    entries: LogEntry[] = []
    categories: Map<string, LogCategory> = new Map()
    isEnabled: boolean = true

    private sessionId: string
    private logCounter: number = 0

    constructor() {
        this.sessionId = this.generateSessionId()

        makeObservable(this, {
            entries: observable,
            categories: observable,
            isEnabled: observable,

            log: action,
            addCategory: action,
            updateCategory: action,
            clear: action,
            setEnabled: action,
        })

        this.registerDefaultCategories()

        if (typeof window !== 'undefined') {
            ;(window as any).__LoggerManager = this
        }
    }

    static getInstance(): LoggerManager {
        if (!LoggerManager.instance) {
            LoggerManager.instance = new LoggerManager()
        }
        return LoggerManager.instance
    }

    // Основні методи логування

    trace(message: string, category: string = 'app', context?: string, data?: any): void {
        this.log('trace', message, category, context, data)
    }

    debug(message: string, category: string = 'app', context?: string, data?: any): void {
        this.log('debug', message, category, context, data)
    }

    info(message: string, category: string = 'app', context?: string, data?: any): void {
        this.log('info', message, category, context, data)
    }

    warn(message: string, category: string = 'app', context?: string, data?: any): void {
        this.log('warn', message, category, context, data)
    }

    error(
        message: string,
        category: string = 'app',
        context?: string,
        data?: any,
        error?: Error
    ): void {
        this.log('error', message, category, context, data, error)
    }

    fatal(
        message: string,
        category: string = 'app',
        context?: string,
        data?: any,
        error?: Error
    ): void {
        this.log('fatal', message, category, context, data, error)
    }

    // Основний метод логування
    log(
        level: LogLevel,
        message: string,
        category: string = 'app',
        context?: string,
        data?: any,
        error?: Error
    ): void {
        if (!this.isEnabled || !this.shouldLog(level, category)) {
            return
        }

        runInAction(() => {
            const entry: LogEntry = {
                id: this.generateLogId(),
                timestamp: Date.now(),
                level,
                message,
                category,
                context,
                data: data ? this.cloneData(data) : undefined,
                error: error
                    ? {
                          name: error.name,
                          message: error.message,
                          stack: error.stack,
                      }
                    : undefined,
                tags: this.extractTags(message, data),
                sessionId: this.sessionId,
            }

            this.entries.push(entry)

            // Обмежуємо кількість записів
            if (this.entries.length > 1000) {
                this.entries = this.entries.slice(-1000)
            }

            // Виводимо в консоль
            // this.logToConsole(entry)
        })
    }

    // Методи для категорій

    addCategory(name: string, category: Omit<LogCategory, 'name'>): void {
        runInAction(() => {
            this.categories.set(name, { name, ...category })
        })
    }

    updateCategory(name: string, updates: Partial<LogCategory>): void {
        runInAction(() => {
            const existing = this.categories.get(name)
            if (existing) {
                const updated = { ...existing, ...updates }
                this.categories.set(name, updated)
            }
        })
    }

    removeCategory(name: string): void {
        runInAction(() => {
            this.categories.delete(name)
        })
    }

    // Отримання даних

    getEntries(filter?: {
        level?: LogLevel | LogLevel[]
        category?: string | string[]
        search?: string
    }): LogEntry[] {
        let filtered = [...this.entries]

        if (filter) {
            if (filter.level) {
                const levels = Array.isArray(filter.level) ? filter.level : [filter.level]
                filtered = filtered.filter((entry) => levels.includes(entry.level))
            }

            if (filter.category) {
                const categories = Array.isArray(filter.category)
                    ? filter.category
                    : [filter.category]
                filtered = filtered.filter((entry) => categories.includes(entry.category))
            }

            if (filter.search) {
                const searchLower = filter.search.toLowerCase()
                filtered = filtered.filter(
                    (entry) =>
                        entry.message.toLowerCase().includes(searchLower) ||
                        entry.category.toLowerCase().includes(searchLower)
                )
            }
        }

        return filtered
    }

    getRecentEntries(count: number = 50): LogEntry[] {
        return this.entries.slice(-count)
    }

    getErrorEntries(count: number = 50): LogEntry[] {
        return this.entries
            .filter((entry) => entry.level === 'error' || entry.level === 'fatal')
            .slice(-count)
    }

    // Статистика

    getStats(): LogStats {
        const byLevel = this.entries.reduce(
            (acc, entry) => {
                acc[entry.level] = (acc[entry.level] || 0) + 1
                return acc
            },
            {} as Record<LogLevel, number>
        )

        const byCategory = this.entries.reduce(
            (acc, entry) => {
                acc[entry.category] = (acc[entry.category] || 0) + 1
                return acc
            },
            {} as Record<string, number>
        )

        const errors = (byLevel.error || 0) + (byLevel.fatal || 0)
        const warnings = byLevel.warn || 0
        const total = this.entries.length

        return {
            total,
            byLevel,
            byCategory,
            errors,
            warnings,
            recentActivity: this.getRecentEntries(20),
        }
    }

    // Управління

    setEnabled(enabled: boolean): void {
        runInAction(() => {
            this.isEnabled = enabled
        })
    }

    clear(): void {
        runInAction(() => {
            this.entries = []
        })
    }

    export(format: 'json' | 'txt' = 'json'): string {
        switch (format) {
            case 'json':
                return JSON.stringify(this.entries, null, 2)
            case 'txt':
                return this.entries
                    .map((entry) => {
                        const timestamp = new Date(entry.timestamp).toISOString()
                        const level = entry.level.toUpperCase().padEnd(5)
                        const category = `[${entry.category}]`.padEnd(12)
                        return `${timestamp} ${level} ${category} ${entry.message}`
                    })
                    .join('\n')
            default:
                return JSON.stringify(this.entries, null, 2)
        }
    }

    // Приватні методи

    private shouldLog(level: LogLevel, category: string): boolean {
        const categoryConfig = this.categories.get(category)
        if (categoryConfig && !categoryConfig.enabled) {
            return false
        }
        return true
    }

    private logToConsole(entry: LogEntry): void {
        const message = `[${entry.category}] ${entry.message}`

        switch (entry.level) {
            case 'trace':
            case 'debug':
                console.debug(message, entry.data)
                break
            case 'info':
                console.info(message, entry.data)
                break
            case 'warn':
                console.warn(message, entry.data)
                break
            case 'error':
            case 'fatal':
                console.error(message, entry.data, entry.error)
                break
        }
    }

    private extractTags(message: string, data?: any): string[] {
        const tags: string[] = []

        // Витягуємо теги з повідомлення ([tag])
        const tagMatches = message.match(/\[([^\]]+)\]/g)
        if (tagMatches) {
            tags.push(...tagMatches.map((tag) => tag.slice(1, -1)))
        }

        // Додаємо теги з даних
        if (data && typeof data === 'object' && data.tags && Array.isArray(data.tags)) {
            tags.push(...data.tags)
        }

        return [...new Set(tags)]
    }

    private cloneData(data: any): any {
        try {
            return JSON.parse(JSON.stringify(data))
        } catch {
            return String(data)
        }
    }

    private generateSessionId(): string {
        return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }

    private generateLogId(): string {
        return `log_${this.sessionId}_${++this.logCounter}`
    }

    private registerDefaultCategories(): void {
        const defaultCategories: Array<[string, Omit<LogCategory, 'name'>]> = [
            [
                'app',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#2196F3',
                    description: 'Основні логи додатку',
                },
            ],
            [
                'ui',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#4CAF50',
                    description: 'UI компоненти та взаємодії',
                },
            ],
            [
                'api',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#FF9800',
                    description: 'API запити та відповіді',
                },
            ],
            [
                'auth',
                {
                    level: 'info',
                    enabled: true,
                    color: '#9C27B0',
                    description: 'Автентифікація та авторизація',
                },
            ],
            [
                'router',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#00BCD4',
                    description: 'Навігація та роутинг',
                },
            ],
            [
                'store',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#795548',
                    description: 'Управління станом',
                },
            ],
            [
                'performance',
                {
                    level: 'info',
                    enabled: true,
                    color: '#607D8B',
                    description: 'Метрики продуктивності',
                },
            ],
            [
                'error',
                {
                    level: 'error',
                    enabled: true,
                    color: '#F44336',
                    description: 'Помилки та винятки',
                },
            ],
            [
                'security',
                { level: 'warn', enabled: true, color: '#E91E63', description: 'Безпека' },
            ],
            [
                'loader',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#3F51B5',
                    description: 'Завантажувачі конфігурацій та ресурсів',
                },
            ],
            [
                'event',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#009688',
                    description: 'Система подій',
                },
            ],
            [
                'debug',
                {
                    level: 'debug',
                    enabled: true,
                    color: '#666666',
                    description: 'Налагоджувальна інформація',
                },
            ],
        ]

        defaultCategories.forEach(([name, category]) => {
            this.addCategory(name, category)
        })
    }
}

// Експортуємо singleton instance
export const loggerManager = LoggerManager.getInstance()
